import { Button } from "@/components/ui/baseUi/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { errorToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { useBoxSlotAddParticipant, useBoxSlotGetNonParticipants } from "@/modules/boxSlot/hooks/useBoxSlotSubscriber"
import { ProfileAvatar } from "@/modules/profile/common/components/profileAvatar"
import SessionEmptySVG from "@images/empty/session_empty.svg"
import { CheckmarkIcon } from "@images/svgIcons/checkmarkIcon"
import { word } from "@modules/core/utils/i18n"
import useDebounce from "@modules/hooks/useDebounce"
import { UserEntity } from "@modules/profile/userProfile/userEntity"
import { LoaderCircle, Search, X } from "lucide-react"
import { useCallback, useImperativeHandle, useMemo, useState } from "react"

export type BoxSessionAddSubscriberModalRef = {
  open: () => void
}

type Props = {
  slotId: string
  ref: React.Ref<BoxSessionAddSubscriberModalRef>
}

export function BoxSessionAddSubscriberModal({ slotId, ref }: Props) {
  const [showModal, setShowModal] = useState(false)
  const [selectedMembers, setSelectedMembers] = useState<UserEntity[]>([])
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const debouncedSearchTerm = useDebounce(searchTerm, 300)
  const { mutateAsync: addParticipantToSlot } = useBoxSlotAddParticipant()
  const { data: nonParticipants, isPending: isPendingNonParticipants } = useBoxSlotGetNonParticipants({
    slotId,
    name: debouncedSearchTerm,
  })

  const addMember = async () => {
    setIsSubmitting(true)
    try {
      for (const member of selectedMembers) {
        await addParticipantToSlot({ slotId, memberId: member.id })
      }
      onHide()
    } catch (e: unknown) {
      if (
        typeof e === "object" &&
        e !== null &&
        "code" in e &&
        (e as { code?: string }).code === "NO_AVAILABLE_PURCHASE"
      ) {
        toast.error(word("boxSessionAddSubscriberModal.dialog.error.noAvailablePurchase"), {
          classNames: errorToastClassName(),
        })
      } else {
        toast.error(e instanceof Error ? e.message : String(e), {
          classNames: errorToastClassName(),
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const filteredResult = useMemo(() => {
    if (!nonParticipants) return []
    return nonParticipants.content.filter(
      (nonParticipant) => !selectedMembers.some((selected) => selected.id === nonParticipant.id),
    )
  }, [nonParticipants, selectedMembers])

  const onHide = useCallback(() => {
    setSearchTerm("")
    setShowModal(false)
  }, [])

  const handleMemberClick = (member: UserEntity) => {
    setSelectedMembers((prev) => [...prev, member])
    setSearchTerm("")
  }

  const handleSelectedMemberClick = (member: UserEntity) => {
    setSelectedMembers((prev) => prev.filter((p) => p.id !== member.id))
  }

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
      setSelectedMembers([])
    },
  }))

  return (
    <Dialog open={showModal} onOpenChange={onHide}>
      <DialogContent className="w-150" aria-describedby={undefined}>
        <DialogHeader>
          <div className="flex shrink flex-col gap-2">
            <DialogTitle>
              <div className="flex flex-col flex-wrap content-center justify-center gap-2 text-center">
                <Text fontStyle="TL" className="text-textDefault-body">
                  {word("boxSessionAddSubscriberModal.dialog.title")}
                </Text>
                <Text fontStyle="BM" className="text-textDefault-caption">
                  {word("boxSessionAddSubscriberModal.dialog.subtitle")}
                </Text>
              </div>
            </DialogTitle>
            <InputGroup>
              <InputGroupInput
                placeholder={word("boxSessionAddSubscriberModal.dialog.search.placeholder")}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                value={searchTerm}
              />
              <InputGroupAddon align="inline-start">
                <Search />
              </InputGroupAddon>
              {searchTerm && (
                <InputGroupAddon align="inline-end">
                  <X className="size-4 cursor-pointer" onClick={() => setSearchTerm("")} />
                </InputGroupAddon>
              )}
            </InputGroup>
          </div>
        </DialogHeader>
        <div className="flex flex-1 flex-col">
          {isPendingNonParticipants ? (
            <div className="flex h-53 flex-col items-center justify-center">
              <LoaderCircle className="animate-spin" />
            </div>
          ) : selectedMembers.length === 0 && searchTerm.length === 0 ? (
            emptyTemplate()
          ) : searchTerm.length > 0 ? (
            <div className="flex h-53 flex-col">
              <Text fontStyle="LS" className="mt-2 mb-4 flex text-textDefault-body uppercase">
                {word("boxSessionAddSubscriberModal.dialog.searchTerm")}
              </Text>
              {/* IF filteredResult is empty : do empty screen*/}
              {filteredResult.length === 0 ? (
                noResult()
              ) : (
                /* Else : do the list of filteredResult */
                <div className="flex-1 overflow-y-auto">
                  {filteredResult.map((member) => itemTemplate(member, handleMemberClick))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex h-53 w-full flex-col">
              <Text fontStyle="LS" className="mt-2 mb-4 flex text-textDefault-body uppercase">
                {word("boxSessionAddSubscriberModal.dialog.selectedTerm")}
              </Text>
              <div className="flex w-full flex-col gap-4 overflow-y-auto pr-2">
                {selectedMembers.map((member) => selectedTemplate(member, handleSelectedMemberClick))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-end gap-4">
          <Button
            variant="outline"
            onClick={() => {
              onHide()
            }}
            className="flex-1 capitalize"
          >
            {word("global.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void addMember()
            }}
            className="flex-1"
            disabled={isSubmitting || selectedMembers.length === 0}
            isLoading={isSubmitting}
          >
            {isSubmitting && <LoaderCircle className="animate-spin" />}
            {word("boxSessionAddSubscriberModal.dialog.button.addToList")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const noResult = () => {
  return (
    <div className="flex h-53 flex-col justify-center">
      <Text fontStyle="LL" className="flex justify-center text-center text-textDefault-body">
        {word("boxSessionAddSubscriberModal.dialog.noResult")}
      </Text>
      <div className="mt-6 flex items-center justify-center text-center">
        <img src={SessionEmptySVG} alt="SessionEmptySVG" />
      </div>
    </div>
  )
}

const emptyTemplate = () => {
  return (
    <div className="flex h-53 flex-col justify-center">
      <Text fontStyle="LL" className="flex justify-center text-center text-textDefault-body">
        {word("boxSessionAddSubscriberModal.dialog.empytemplate.body")}
      </Text>
      <div className="mt-6 flex items-center justify-center text-center">
        <img src={SessionEmptySVG} alt="SessionEmptySVG" />
      </div>
    </div>
  )
}

const itemTemplate = (member: UserEntity, onClick: (member: UserEntity) => void) => {
  return (
    <div
      className="flex w-full cursor-pointer border-b border-neutral-200 py-3 first:pt-0 last:border-b-0"
      onClick={() => onClick(member)}
      key={member.id}
    >
      <ProfileAvatar profile={{ avatar: member.avatar, username: member.firstname }} />
      <div className="ml-2 flex flex-1 flex-col justify-center gap-1 overflow-hidden">
        <Text fontStyle="LL" className="truncate text-textDefault-body">
          {`${member.firstname} ${member.lastname}`}
        </Text>
        <Text fontStyle="BS" className="truncate text-textDefault-caption">
          {`${member.username}`}
        </Text>
      </div>
    </div>
  )
}

const selectedTemplate = (member: UserEntity, onClick: (member: UserEntity) => void) => {
  return (
    <div
      className="flex w-full cursor-pointer items-center gap-3 rounded-full bg-gray-900 p-2 last:mb-4"
      onClick={() => onClick(member)}
      key={member.id}
    >
      <ProfileAvatar profile={{ avatar: member.avatar, username: member.firstname }} />
      <div className="flex flex-1 flex-col justify-center gap-1 overflow-hidden">
        <Text fontStyle="LL" className="truncate text-textDefault-body">
          {`${member.firstname} ${member.lastname}`}
        </Text>
        <Text fontStyle="BS" className="truncate text-textDefault-caption">
          {`${member.username}`}
        </Text>
      </div>
      <CheckmarkIcon className="size-6 shrink-0" color="currentColor" />
    </div>
  )
}
